/**
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 22/10/23
 */

import React from 'react';
import { HStack, Heading, Box, Container, UnorderedList, ListItem, Text } from '@chakra-ui/react';

interface ListDisplayProps<T> {
    heading: string;
    listItems: T[];
    renderItem: (item: T) => React.ReactNode;
    minItems?: number;
    maxHeight?: string;
    placeholder?: string;
    id: keyof T;
}

const ListDisplay = <T,>({
    heading,
    listItems,
    renderItem,
    minItems = 1,
    placeholder,
    maxHeight = '100px',
    id,
}: ListDisplayProps<T>) => {
    if (listItems.length < minItems) return null;

    return (
        <HStack align="flex-start" justifyContent="space-between" gap={1}>
            <Heading as="h3" fontSize="md">
                {heading}
            </Heading>
            <Box>
                <Container
                    border="2px solid"
                    borderColor="gray.500"
                    minWidth="180px"
                    maxHeight={maxHeight}
                    overflowY="auto"
                    scrollBehavior="smooth"
                    paddingY={2}
                >
                    <UnorderedList listStyleType="none" m={0}>
                        {listItems.map((item) => (
                            <ListItem key={`${item[id]}`} fontSize="sm">
                                {renderItem(item)}
                            </ListItem>
                        ))}
                    </UnorderedList>
                    {listItems.length === 0 && !!placeholder && (
                        <Text fontSize="sm" color="gray.900" fontStyle="italic">
                            {placeholder}
                        </Text>
                    )}
                </Container>
            </Box>
        </HStack>
    );
};

export default ListDisplay;
